import { sanitizePdfText } from "helpers";
import { t } from "i18next";
import { RowInput, Styles } from "jspdf-autotable";

import { groupReturnVariantsByPack } from "../groupReturnVariantsByPack";
import {
  ReturnDetails,
  ReturnProductVariant,
  ReturnProductVariantSizeItem
} from "../../returns.types";

interface getProductsProps {
  returnDetails: ReturnDetails;
  normalFontStyles: Partial<Styles>;
}

const PACK_FILL_COLOR = "#F5F5F5";
const PACK_ITEM_LEFT_PADDING = 18;
const DEFAULT_HORIZONTAL_PADDING = 5;

interface CellStyleOptions {
  fillColor?: string;
  leftPadding?: number;
}

export const getProducts = ({
  returnDetails,
  normalFontStyles
}: getProductsProps) => {
  const cellStyles = (
    halign: "left" | "right",
    isLast: boolean,
    options?: CellStyleOptions
  ): Partial<Styles> => {
    const left = options?.leftPadding ?? DEFAULT_HORIZONTAL_PADDING;
    const styles: Partial<Styles> = {
      valign: "middle",
      halign,
      ...normalFontStyles,
      cellPadding: {
        top: 1,
        bottom: isLast ? 5 : 1,
        left,
        right: DEFAULT_HORIZONTAL_PADDING
      }
    };
    if (options?.fillColor) {
      styles.fillColor = options.fillColor;
    }
    return styles;
  };

  const formatStandaloneLine = (
    productVariant: ReturnProductVariant,
    item: ReturnProductVariantSizeItem
  ): string => {
    const detail = `${t("Size")}: ${item.sizeValue}  •  ${t("Color")}: ${t(productVariant.colorValue)}  •  ${t("Qty")}: ${item.returnQuantity}`;
    return `${sanitizePdfText(productVariant.name)}\n${detail}`;
  };

  const formatPackSlotLine = (
    productVariant: ReturnProductVariant,
    item: ReturnProductVariantSizeItem
  ): string => {
    const slotPrefix = item.packSlotTitle
      ? `${sanitizePdfText(item.packSlotTitle)} — `
      : "";
    const detail = `${t("Size")}: ${item.sizeValue}  •  ${t("Color")}: ${t(productVariant.colorValue)}  •  ${t("Qty")}: ${item.returnQuantity}`;
    return `${slotPrefix}${sanitizePdfText(productVariant.name)}\n${detail}`;
  };

  const grouped = groupReturnVariantsByPack(returnDetails);
  const products: RowInput[] = [];

  grouped.forEach((entry, entryIndex) => {
    const isLastEntry = entryIndex === grouped.length - 1;

    if (entry.kind === "standalone") {
      entry.items.forEach((item, itemIndex) => {
        const isLastRow = isLastEntry && itemIndex === entry.items.length - 1;
        products.push([
          {
            content: formatStandaloneLine(entry.productVariant, item),
            styles: cellStyles("left", isLastRow)
          }
        ]);
      });
      return;
    }

    const packTitle = entry.packTitle
      ? `📦 ${t("Pack")} · ${sanitizePdfText(entry.packTitle)}`
      : `📦 ${t("Pack")} #${entryIndex + 1}`;

    const hasRows = entry.items.length > 0;
    const headerIsLast = isLastEntry && !hasRows;

    products.push([
      {
        content: packTitle,
        styles: cellStyles("left", headerIsLast, { fillColor: PACK_FILL_COLOR })
      }
    ]);

    entry.items.forEach(({ productVariant, item }, rowIndex) => {
      const isLastRow = rowIndex === entry.items.length - 1;
      const cellIsLast = isLastEntry && isLastRow;
      products.push([
        {
          content: formatPackSlotLine(productVariant, item),
          styles: cellStyles("left", cellIsLast, {
            fillColor: PACK_FILL_COLOR,
            leftPadding: PACK_ITEM_LEFT_PADDING
          })
        }
      ]);
    });
  });

  return { products };
};
